import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Globe, Youtube, RotateCw, FileText, Plus, Pencil, Trash2, Check } from "lucide-react";
import MarkdownEditor from "@/components/MarkdownEditor";

const CANALI = [
  { canale: "sito_web", label: "Sito web" },
  { canale: "autoscout24", label: "AutoScout24" },
  { canale: "subito", label: "Subito.it" },
  { canale: "automobile_it", label: "Automobile.it" },
  { canale: "autosupermarket", label: "AutoSuperMarket" },
];

const BADGES = [
  { key: "km_certificati", label: "Km certificati" },
  { key: "garanzia_inclusa", label: "Garanzia inclusa" },
  { key: "auto_di_privato", label: "Auto di privato" },
  { key: "visibile_su_appuntamento", label: "Visibile su appuntamento" },
  { key: "super_prezzo", label: "Super prezzo" },
  { key: "promo", label: "Promo" },
  { key: "scelto_per_voi", label: "Scelto per voi" },
];

const SectionTitle = ({ children }) => (
  <div className="font-heading text-sm uppercase text-slate-500 tracking-wide mb-3">{children}</div>
);

// Modal gestione modelli di testo (CRUD, scope azienda)
function ManageTemplates({ open, onClose, companyId, onChanged }) {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null); // {id?, nome, contenuto}

  const load = useCallback(() => {
    if (!companyId) return;
    api.get(`/web/text-templates?company_id=${companyId}`).then((r) => setRows(r.data || [])).catch(() => {});
  }, [companyId]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const save = async () => {
    if (!editing?.nome?.trim()) { toast.error("Nome modello obbligatorio"); return; }
    try {
      if (editing.id) {
        await api.patch(`/web/text-templates/${editing.id}`, { nome: editing.nome, contenuto: editing.contenuto });
        toast.success("Modello aggiornato");
      } else {
        await api.post(`/web/text-templates`, { company_id: companyId, nome: editing.nome, contenuto: editing.contenuto || "" });
        toast.success("Modello creato");
      }
      setEditing(null); load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore salvataggio"); }
  };

  const del = async (id) => {
    try { await api.delete(`/web/text-templates/${id}`); toast.info("Modello eliminato"); load(); onChanged?.(); }
    catch { toast.error("Errore eliminazione"); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { setEditing(null); onClose(); } }}>
      <DialogContent className="max-w-2xl" data-testid="manage-templates-dialog">
        <DialogHeader><DialogTitle>Modelli di testo · azienda</DialogTitle></DialogHeader>
        {editing ? (
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-slate-600">Nome modello</Label>
              <Input value={editing.nome} onChange={(e) => setEditing({ ...editing, nome: e.target.value })}
                     placeholder="es. Sito, Autoscout…" data-testid="template-nome-input" />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Contenuto</Label>
              <MarkdownEditor value={editing.contenuto} onChange={(v) => setEditing({ ...editing, contenuto: v })}
                              placeholder="Scrivi il modello…" testId="template-contenuto-editor" rows={6} />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditing(null)} data-testid="template-cancel-btn">Annulla</Button>
              <Button onClick={save} className="bg-blue-600 hover:bg-blue-700" data-testid="template-save-btn">
                <Check className="h-4 w-4 mr-1" /> Salva
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex justify-end">
              <Button size="sm" onClick={() => setEditing({ nome: "", contenuto: "" })} data-testid="template-new-btn">
                <Plus className="h-4 w-4 mr-1" /> Nuovo modello
              </Button>
            </div>
            {rows.length === 0 ? (
              <div className="py-8 text-center text-slate-400 text-sm">Nessun modello. Creane uno.</div>
            ) : (
              <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg">
                {rows.map((t) => (
                  <div key={t.id} className="flex items-center gap-2 px-3 py-2" data-testid={`template-row-${t.id}`}>
                    <FileText className="h-4 w-4 text-slate-400" />
                    <span className="flex-1 font-medium text-sm truncate">{t.nome}</span>
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditing({ id: t.id, nome: t.nome, contenuto: t.contenuto })} data-testid={`template-edit-${t.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-600" onClick={() => del(t.id)} data-testid={`template-del-${t.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Blocco descrizione con picker modello
function DescrizioneBlock({ label, value, onChange, companyId, refreshKey, canEdit, testIdPrefix }) {
  const [templates, setTemplates] = useState([]);
  useEffect(() => {
    if (!companyId) return;
    api.get(`/web/text-templates?company_id=${companyId}`).then((r) => setTemplates(r.data || [])).catch(() => {});
  }, [companyId, refreshKey]);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-xs text-slate-600">{label}</Label>
        <select
          className="text-xs border border-slate-200 rounded-md px-2 py-1 bg-white text-slate-600"
          value=""
          disabled={!canEdit}
          onChange={(e) => {
            const t = templates.find((x) => x.id === e.target.value);
            if (t) onChange(t.contenuto || "");
            e.target.value = "";
          }}
          data-testid={`${testIdPrefix}-template-picker`}
        >
          <option value="">Usa un modello…</option>
          {templates.map((t) => <option key={t.id} value={t.id}>{t.nome}</option>)}
        </select>
      </div>
      <MarkdownEditor value={value} onChange={onChange} disabled={!canEdit}
                      placeholder="Scrivi la descrizione dell'annuncio…" testId={`${testIdPrefix}-editor`} />
    </div>
  );
}

export default function VehicleWebTab({ vehicle, form, set, canEdit, onReload }) {
  const companyId = vehicle?.company_id;
  const [canali, setCanali] = useState([]); // config azienda
  const [tplRefresh, setTplRefresh] = useState(0);
  const [manageOpen, setManageOpen] = useState(false);

  useEffect(() => {
    if (!companyId) return;
    api.get(`/companies/${companyId}/canali-pubblicazione`).then((r) => setCanali(r.data?.canali || [])).catch(() => {});
  }, [companyId]);

  const attiviSet = new Set(canali.filter((c) => c.attivo).map((c) => c.canale));
  const pubblicatoSet = new Set((vehicle?.portali_pubblicazione || []).filter((p) => p.pubblicato).map((p) => p.canale));

  const toggleCanale = async (canale, next) => {
    try {
      await api.patch(`/vehicles/${vehicle.id}/pubblicazione`, { canale, pubblicato: next });
      toast.success(next ? "Pubblicato" : "Rimosso dalla pubblicazione");
      onReload?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Errore");
    }
  };

  const badges = form.web_badges || {};
  const setBadge = (key, val) => set({ web_badges: { ...badges, [key]: val } });
  const ivaAttivabile = !!vehicle?.iva_web_attivabile;

  return (
    <div className="space-y-4" data-testid="vehicle-web-tab">
      {/* PUBBLICATO SU */}
      <Card className="p-5">
        <SectionTitle>Pubblicato su</SectionTitle>
        <div className="flex flex-wrap gap-3">
          {CANALI.map((c) => {
            const attivo = attiviSet.has(c.canale);
            const checked = pubblicatoSet.has(c.canale);
            return (
              <label
                key={c.canale}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm select-none transition-colors ${
                  !attivo ? "border-slate-100 bg-slate-50 text-slate-300 cursor-not-allowed"
                  : checked ? "border-blue-300 bg-blue-50 text-blue-800 cursor-pointer"
                  : "border-slate-200 text-slate-700 hover:border-slate-300 cursor-pointer"
                }`}
                title={attivo ? "" : "Canale non attivo per questa azienda"}
              >
                <Checkbox
                  checked={checked}
                  disabled={!attivo || !canEdit}
                  onCheckedChange={(v) => toggleCanale(c.canale, v === true)}
                  data-testid={`web-canale-${c.canale}`}
                />
                <Globe className="h-3.5 w-3.5 opacity-60" />
                <span>{c.label}</span>
              </label>
            );
          })}
        </div>
        <p className="text-xs text-slate-400 mt-3">I canali non attivi per l'azienda sono disabilitati. Attivali in Impostazioni azienda → Canali di pubblicazione.</p>
      </Card>

      {/* LINK + MOSTRA LISTINO */}
      <Card className="p-5">
        <SectionTitle>Link e opzioni</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-slate-600 flex items-center gap-1"><Youtube className="h-3.5 w-3.5" /> Link YouTube</Label>
            <Input value={form.link_youtube || ""} disabled={!canEdit}
                   onChange={(e) => set({ link_youtube: e.target.value })}
                   placeholder="https://youtube.com/…" data-testid="web-link-youtube" />
          </div>
          <div>
            <Label className="text-xs text-slate-600 flex items-center gap-1"><RotateCw className="h-3.5 w-3.5" /> Link 360°</Label>
            <Input value={form.link_360 || ""} disabled={!canEdit}
                   onChange={(e) => set({ link_360: e.target.value })}
                   placeholder="https://…" data-testid="web-link-360" />
          </div>
        </div>
        <label className="flex items-center gap-2 mt-4 text-sm text-slate-700 cursor-pointer select-none">
          <Checkbox checked={!!form.mostra_listino} disabled={!canEdit}
                    onCheckedChange={(v) => set({ mostra_listino: v === true })} data-testid="web-mostra-listino" />
          <span>Mostra listino sul sito</span>
        </label>
        {/* Indicazioni IVA — attivabili solo se il veicolo è in regime IVA */}
        <div className="mt-4 pt-4 border-t border-slate-100">
          <div className="text-xs text-slate-500 mb-2">Indicazioni IVA sull'annuncio {!ivaAttivabile && <span className="text-amber-600">(veicolo non in regime IVA → non attivabili)</span>}</div>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <label className={`flex items-center gap-2 text-sm select-none ${ivaAttivabile && canEdit ? "text-slate-700 cursor-pointer" : "text-slate-300 cursor-not-allowed"}`}>
              <Checkbox checked={!!form.iva_esposta_web} disabled={!ivaAttivabile || !canEdit}
                        onCheckedChange={(v) => set({ iva_esposta_web: v === true })} data-testid="web-iva-esposta" />
              <span>IVA esposta</span>
            </label>
            <label className={`flex items-center gap-2 text-sm select-none ${ivaAttivabile && canEdit ? "text-slate-700 cursor-pointer" : "text-slate-300 cursor-not-allowed"}`}>
              <Checkbox checked={!!form.prezzo_comprensivo_iva_web} disabled={!ivaAttivabile || !canEdit}
                        onCheckedChange={(v) => set({ prezzo_comprensivo_iva_web: v === true })} data-testid="web-iva-compresa" />
              <span>Prezzo comprensivo di IVA</span>
            </label>
          </div>
        </div>
      </Card>

      {/* BADGE */}
      <Card className="p-5">
        <SectionTitle>Badge (spunta manuale)</SectionTitle>
        <div className="flex flex-wrap gap-x-6 gap-y-3">
          {BADGES.map((b) => (
            <label key={b.key} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none">
              <Checkbox checked={!!badges[b.key]} disabled={!canEdit}
                        onCheckedChange={(v) => setBadge(b.key, v === true)} data-testid={`web-badge-${b.key}`} />
              <span>{b.label}</span>
            </label>
          ))}
        </div>
        <p className="text-xs text-slate-400 mt-3">I badge sono solo un dato: non vengono stampati sulle foto (li disegna il sito).</p>
      </Card>

      {/* DESCRIZIONE ANNUNCIO */}
      <Card className="p-5">
        <div className="flex items-center justify-between mb-3">
          <SectionTitle>Descrizione annuncio</SectionTitle>
          <Button size="sm" variant="outline" onClick={() => setManageOpen(true)} data-testid="web-manage-templates-btn">
            <FileText className="h-4 w-4 mr-1" /> Gestisci modelli
          </Button>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <DescrizioneBlock label="Testo per il Sito" value={form.testo_sito || ""}
                            onChange={(v) => set({ testo_sito: v })} companyId={companyId}
                            refreshKey={tplRefresh} canEdit={canEdit} testIdPrefix="web-testo-sito" />
          <DescrizioneBlock label="Testo per i Portali" value={form.testo_portali || ""}
                            onChange={(v) => set({ testo_portali: v })} companyId={companyId}
                            refreshKey={tplRefresh} canEdit={canEdit} testIdPrefix="web-testo-portali" />
        </div>
        <p className="text-xs text-slate-400 mt-3">Formattazione salvata in Markdown (grassetto, corsivo, elenchi). Il prezzo promo si descrive nel testo e si evidenzia con il badge Promo.</p>
      </Card>

      <ManageTemplates open={manageOpen} onClose={() => setManageOpen(false)}
                       companyId={companyId} onChanged={() => setTplRefresh((n) => n + 1)} />
    </div>
  );
}
